import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@components/atoms/Card';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import styles from './PromoBasketCard.module.css';
import { formatPrice } from '@utils/format';
import { SITE_URLS } from '@constants';

export interface PromoBasketCardProps {
    uuid: string;
    merchants: string[];
    name: string;
    image: string;
    price: number;
    oldPrice?: number;
    promo?: number;
}

export const PromoBasketCard: FC<PromoBasketCardProps> = ({ uuid, merchants, name, image, price, oldPrice, promo }) => {
    const { t } = useTranslation();

    const translations = {
        merchantsLabel: t('promo-basket-card-component.merchants.label'),
    };

    return (
        <Card classes={styles['promo-basket-card']}>
            <div className={styles.overlay}>
                {/* promo percentage */}
                <div className={`${styles.promo} align-content-center text-center ${promo ? 'bg-dark' : ''}`}>
                    <Typography tagVariant={TypographyTagVariants.span}>
                        <>{promo && `${promo}%`}</>
                    </Typography>
                </div>
            </div>
            <Anchor
                href={SITE_URLS.promoBasketDetail.replace(':uuid', `${uuid}`)}
                classes="text-decoration-none"
                mode={AnchorMode.none}>
                <>
                    <div className="position-relative mb-3">
                        <Card.ImageOverlay />
                        <img src={image} className="card-img-top" alt={name} />
                    </div>
                    <div className="card-body px-0 pt-3">
                        <Card.Title
                            typography={{
                                tagVariant: TypographyTagVariants.h5,
                                classes: `${styles.title} text-truncate mb-2`,
                                color: 'dark',
                                fontWeight: '900',
                                fontSize: '16',
                                children: name,
                            }}
                            underlineAnimation={true}
                        />
                        <Typography classes="mt-1 mb-1" color="dark" fontSize="16">
                            <>
                                <Typography color="muted" fontSize="16" tagVariant={TypographyTagVariants.span}>
                                    {`${translations.merchantsLabel}: `}
                                </Typography>
                                {merchants.map((merchant, index) => (
                                    <Fragment key={`merchant-${index}`}>
                                        {index !== 0 && (
                                            <Typography color="muted" fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                {' ∙ '}
                                            </Typography>
                                        )}
                                        {merchant}
                                    </Fragment>
                                ))}
                            </>
                        </Typography>
                        {oldPrice !== undefined && (
                            <Typography classes="d-inline-block text-decoration-line-through me-2" color="primary" fontSize="16">
                                {formatPrice(oldPrice)}
                            </Typography>
                        )}
                        <Typography classes="d-inline-block" color="primary" fontWeight="900" fontSize="16">
                            {formatPrice(price)}
                        </Typography>
                    </div>
                </>
            </Anchor>
        </Card>
    );
};
